// client/src/components/CookieConsent.js
import React, { useState, useEffect } from 'react';
import '../styles/CookieConsent.css';
import { enableGA, disableGA, initializeGA } from '../services/analyticsService';

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    functional: true, // Always enabled
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const savedPreferences = localStorage.getItem('cookiePreferences');
    
    if (savedPreferences === null) {
      // If no choice has been made, show the banner and disable GA
      setVisible(true);
      disableGA();
    } else {
      // Load saved preferences
      const preferences = JSON.parse(savedPreferences);
      setCookiePreferences(preferences);
      
      // Enable/disable services based on preferences
      if (preferences.analytics) {
        enableGA();
        initializeGA();
      } else {
        disableGA();
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      functional: true,
      analytics: true,
      marketing: true
    };
    
    // Save preferences
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    localStorage.setItem('cookieConsent', 'accepted'); // Keep for backwards compatibility
    localStorage.setItem('marketingCookies', 'true'); // Enable marketing features
    
    setCookiePreferences(allAccepted);
    setVisible(false);
    
    // Enable analytics
    enableGA();
    initializeGA();
  };

  const handleEssentialOnly = () => {
    const essentialOnly = {
      functional: true,
      analytics: false,
      marketing: false
    };
    
    // Save preferences
    localStorage.setItem('cookiePreferences', JSON.stringify(essentialOnly));
    localStorage.setItem('cookieConsent', 'declined'); // Keep for backwards compatibility
    localStorage.setItem('marketingCookies', 'false'); // Disable marketing features
    
    setCookiePreferences(essentialOnly);
    setVisible(false);
    
    // Ensure GA remains disabled
    disableGA();
  };

  const handleCustomPreferences = (e) => {
    const { name, checked } = e.target;
    const newPreferences = {
      ...cookiePreferences,
      [name]: checked
    };
    
    setCookiePreferences(newPreferences);
  };

  const handleSavePreferences = () => {
    // Save custom preferences
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    localStorage.setItem('cookieConsent', cookiePreferences.analytics ? 'accepted' : 'declined');
    localStorage.setItem('marketingCookies', cookiePreferences.marketing.toString());
    
    setVisible(false);
    
    // Enable/disable analytics based on preference
    if (cookiePreferences.analytics) {
      enableGA();
      initializeGA();
    } else {
      disableGA();
    }
  };

  if (!visible) {
    return null;
  }

  return (
    <div className="cookie-consent">
      <div className="cookie-content">
        <div className="cookie-text">
          <h3>Cookie Preferences</h3>
          <p>
            We use cookies and similar technologies to provide certain features, 
            enhance the user experience and deliver content that is relevant to your interests.
            Please select your preferences below. Read more in our{' '}
            <a href="/legal/cookies.html">Cookie Policy</a> and{' '}
            <a href="/legal/privacy_policy.html">Privacy Policy</a>.
          </p>
          
          <div className="cookie-options">
            <label>
              <input
                type="checkbox"
                checked={cookiePreferences.functional}
                disabled
              />
              Essential Cookies (Always enabled)
            </label>
            
            <label>
              <input
                type="checkbox"
                name="analytics"
                checked={cookiePreferences.analytics}
                onChange={handleCustomPreferences}
              />
              Analytics Cookies
            </label>
            
            <label>
              <input
                type="checkbox"
                name="marketing"
                checked={cookiePreferences.marketing}
                onChange={handleCustomPreferences}
              />
              Marketing Cookies
            </label>
          </div>
        </div>
        
        <div className="cookie-buttons">
          <button className="button secondary-button" onClick={handleEssentialOnly}>
            Essential Only
          </button>
          <button className="button secondary-button" onClick={handleSavePreferences}>
            Save Preferences
          </button>
          <button className="button" onClick={handleAcceptAll}>
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;